import { useMemo } from "react";
import Chart from "react-apexcharts";
import { useChartTheme } from "../../../../hooks/useChartTheme";
import styles from "../../../../styles/NavigationLogs.module.css";
import { GetNavigationLogsDto } from "./types";

interface LogsChartsProps {
  logs: GetNavigationLogsDto[];
}

export const LogsCharts = ({ logs }: LogsChartsProps) => {
  const { baseOptions, semantic } = useChartTheme();

  /** Cada tipo de log tem um significado próprio, daí a cor semântica. */
  const typeColorFor = (type: string) =>
    ({
      NAVIGATION: semantic.info,
      CLICK: semantic.success,
      API: semantic.warning,
      ERROR: semantic.danger,
    }[type] ?? semantic.neutral);

  const { typeLabels, typeCounts, typeColors } = useMemo(() => {
    const map: Record<string, number> = {};
    logs.forEach((l) => { map[l.type] = (map[l.type] ?? 0) + 1; });
    const labels = Object.keys(map);
    return {
      typeLabels: labels,
      typeCounts: labels.map((k) => map[k]),
      typeColors: labels.map(typeColorFor),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, semantic]);

  const { pageLabels, pageAvgs } = useMemo(() => {
    const map: Record<string, number[]> = {};
    logs
      .filter((l) => l.duration != null)
      .forEach((l) => {
        if (!map[l.page]) map[l.page] = [];
        map[l.page].push(l.duration!);
      });
    const pages = Object.keys(map).slice(0, 6);
    return {
      pageLabels: pages,
      pageAvgs: pages.map((p) =>
        Math.round(map[p].reduce((a, b) => a + b, 0) / map[p].length)
      ),
    };
  }, [logs]);

  return (
    <div className={styles.chartsRow}>
      <div className={styles.chartBox}>
        <div className={styles.chartTitle}>Logs por tipo</div>
        <Chart
          type="bar"
          height={170}
          series={[{ name: "Logs", data: typeCounts }]}
          options={{
            ...baseOptions,
            colors: typeColors,
            plotOptions: { bar: { borderRadius: 4, distributed: true, columnWidth: "55%" } },
            xaxis: { categories: typeLabels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "11px" } } },
            yaxis: { labels: { style: { fontSize: "11px" } } },
            legend: { show: false },
          }}
        />
      </div>

      <div className={styles.chartBox}>
        <div className={styles.chartTitle}>Duração média por página (ms)</div>
        <Chart
          type="bar"
          height={170}
          series={[{ name: "Avg ms", data: pageAvgs }]}
          options={{
            ...baseOptions,
            colors: [semantic.info],
            plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "55%" } },
            xaxis: { categories: pageLabels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "10px" } } },
            yaxis: { labels: { style: { fontSize: "11px" }, maxWidth: 120 } },
          }}
        />
      </div>
    </div>
  );
};
